/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useContext, useEffect, useRef, useState } from "react";
import { getData } from "../../Services/getData";
import { Vocabulary } from "../../Utils/Vocabulary";
import genericStyles from "../../Styles/genericStyles.module.css";
import { CustomThemeContext } from "../../Context/CustomThemeProvider";
import styles from "../../Styles/previewPDF.module.css";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { GetApp, ZoomIn, ZoomOut, Print } from "@mui/icons-material";
import { PreviewDocumentProps } from "../../Utils/Types";
import { pdfjs, Document as PdfDocument, Page } from "react-pdf";
import { renderAsync } from "docx-preview";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function PreviewPDF(props: PreviewDocumentProps) {
  const { url, htmlContent, open, onClose, title } = props;
  const contentRef: any = useRef(null);
  const docxContainerRef: any = useRef(null);
  const scaleAmount = 0.5;
  const themeContext: any = useContext(CustomThemeContext);
  const [state, setState] = useState({
    numPages: 0,
    templateLoaded: 0,
    pageNumber: 1,
    scale: 1.75,
    showEmail: false,
    emailSent: false,
    addImage: false,
    docxLoaded: false,
  });
  const [content, setContent] = useState(htmlContent);

  /**
   *
   */
  useEffect(() => {
    if (url && url !== "") {
      // Check if it's a DOCX file
      if (url.match(/\.docx?$/i)) {
        loadDocxFile(url);
      } else {
        getData(url).then((response: any) => {
          if (response?.errors) return;
          setState({
            ...state,
            templateLoaded: 1,
          });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  /**
   *
   */
  useEffect(() => {
    if (!content) {
      setContent(htmlContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent]);

  /**
   *
   * @param data
   */
  const onDocumentLoadSuccess = (data: { numPages: any }) => {
    const { numPages } = data;
    setState({ ...state, numPages });
  };

  /**
   *
   */
  const zoomIn = () => {
    setState({ ...state, scale: state.scale + scaleAmount });
  };

  /**
   *
   */
  const zoomOut = () => {
    setState({ ...state, scale: state.scale - scaleAmount });
  };

  /**
   *
   */
  const renderPages = () => {
    const pages: Array<any> = [];
    for (let i = 0; i < state.numPages; i++) {
      pages.push(
        <Page
          renderTextLayer={false}
          renderAnnotationLayer={false}
          className={styles.pdfPreviewPage}
          key={`page_${i + 1}`}
          scale={state.scale}
          pageNumber={i + 1}
        />
      );
    }
    return pages;
  };

  /**
   * Load and render DOCX file
   */
  const loadDocxFile = async (docxUrl: string) => {
    try {
      const response = await fetch(docxUrl);
      const arrayBuffer = await response.arrayBuffer();

      if (docxContainerRef.current) {
        // Clear previous content
        docxContainerRef.current.innerHTML = '';

        // Render DOCX content
        await renderAsync(arrayBuffer, docxContainerRef.current, undefined, {
          className: "docx",
          inWrapper: false,
          ignoreWidth: true,
          ignoreHeight: true,
          ignoreFonts: false,
          breakPages: false,
          ignoreLastRenderedPageBreak: false,
          experimental: false,
          trimXmlDeclaration: true,
          useBase64URL: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        });

        setState(prev => ({ ...prev, docxLoaded: true, templateLoaded: 1 }));
      }
    } catch (error) {
      console.error('Error loading DOCX file:', error);
      setState(prev => ({ ...prev, templateLoaded: 1 }));
    }
  };

  /**
   * Download file with proper MIME type based on extension
   */
  const downloadPDF = () => {
    if (!url) return;

    // Determine MIME type based on file extension
    const getMimeType = (url: string) => {
      if (url.match(/\.pdf$/i)) return "application/pdf";
      if (url.match(/\.docx$/i)) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      if (url.match(/\.doc$/i)) return "application/msword";
      if (url.match(/\.xlsx$/i)) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (url.match(/\.xls$/i)) return "application/vnd.ms-excel";
      if (url.match(/\.pptx$/i)) return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
      if (url.match(/\.ppt$/i)) return "application/vnd.ms-powerpoint";
      if (url.match(/\.(jpg|jpeg)$/i)) return "image/jpeg";
      if (url.match(/\.png$/i)) return "image/png";
      if (url.match(/\.gif$/i)) return "image/gif";
      if (url.match(/\.svg$/i)) return "image/svg+xml";
      if (url.match(/\.txt$/i)) return "text/plain";
      if (url.match(/\.rtf$/i)) return "application/rtf";
      return "application/octet-stream"; // Default fallback
    };

    // Get filename from URL or use title
    const getFileName = () => {
      if (title && title.trim()) return title;
      const urlParts = url.split('/');
      return urlParts[urlParts.length - 1] || 'document';
    };

    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const mimeType = getMimeType(url);
        const fileName = getFileName();
        const downloadBlob = new Blob([blob], { type: mimeType });
        const link = document.createElement("a");
        link.download = fileName;
        link.href = URL.createObjectURL(downloadBlob);
        link.click();
        // Clean up the object URL
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
      })
      .catch((error) => {
        console.error('Download failed:', error);
      });
  };

  return (
    <Dialog
      style={{ zIndex: 2000 }}
      maxWidth="xl"
      classes={{ paper: styles.overflowHidden }}
      open={open}
      onClose={() => onClose(state.emailSent)}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle
        id="form-dialog-title"
        className={genericStyles.fullWidthElement}
      >
        <div style={{ flexGrow: 2 }}>{title}</div>
        <div>{props.customTopMenu && props.customTopMenu}</div>
      </DialogTitle>
      <DialogContent ref={contentRef} className={styles.dialogContent}>
        {url ? (
          <>
            {/* Image preview support */}
            {url.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i) ? (
              <Box sx={{ position: "relative", minHeight: "80px" }}>
                {!state.templateLoaded && (
                  <div className={styles.loadingWrapper}>
                    <CircularProgress color="secondary" />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Se încarcă...
                    </Typography>
                  </div>
                )}
                <img
                  src={url}
                  alt={title}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "80vh",
                    display: state.templateLoaded ? "block" : "none",
                    margin: "0 auto",
                    borderRadius: 8,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                  onLoad={() => setState({ ...state, templateLoaded: 1 })}
                />
              </Box>
            ) : url.match(/\.docx?$/i) ? (
              /* DOCX documents - render with docx-preview */
              <Box sx={{ position: "relative", minHeight: "300px" }}>
                {!state.templateLoaded && (
                  <div className={styles.loadingWrapper}>
                    <CircularProgress color="secondary" />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Se încarcă documentul DOCX...
                    </Typography>
                  </div>
                )}
                <div
                  ref={docxContainerRef}
                  className={styles.docxContainer}
                  style={{
                    display: state.templateLoaded ? "block" : "none",
                    padding: "20px",
                    backgroundColor: "white",
                    borderRadius: 8,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    minHeight: "400px",
                    maxHeight: "70vh",
                    overflow: "auto",
                    fontSize: `${state.scale * 0.9}em`,
                    lineHeight: 1.5,
                  }}
                />
              </Box>
            ) : url.match(/\.(xlsx?|pptx?|txt|rtf)$/i) ? (
              /* Other Office documents - show download message */
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "300px",
                textAlign: "center",
                gap: 2
              }}>
                <Typography variant="h6" color="text.secondary">
                  Previzualizare indisponibilă
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Acest tip de document nu poate fi previzualizat în browser.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Utilizați butonul de descărcare pentru a vizualiza documentul.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<GetApp />}
                  onClick={downloadPDF}
                  sx={{ mt: 2 }}
                >
                  Descarcă documentul
                </Button>
              </Box>
            ) : !state.templateLoaded ? (
              <div className={styles.loadingWrapper}>
                <CircularProgress color="secondary" />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Se încarcă...
                </Typography>
              </div>
            ) : (
              <PdfDocument file={url} onLoadSuccess={onDocumentLoadSuccess}>
                {renderPages()}
              </PdfDocument>
            )}
          </>
        ) : (
          <div className={styles.loadingWrapper}>
            <CircularProgress color="secondary" />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Se încarcă...
            </Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions classes={{ root: styles.actionsWrapper }}>
        {!props.hideDownloadButton && (
          <IconButton onClick={downloadPDF}>
            <GetApp />
          </IconButton>
        )}
        {!props.hidePrintButton && (
          <IconButton
            onClick={() => {
            if (!url) return;
            if (url.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i)) {
              // Print image
              const printWindow = window.open(url, "_blank");
              printWindow?.addEventListener("load", () => {
                printWindow?.print();
              });
            } else {
              // Print PDF
              const printFrame = document.createElement("iframe");
              printFrame.style.position = "fixed";
              printFrame.style.right = "0";
              printFrame.style.bottom = "0";
              printFrame.style.width = "0";
              printFrame.style.height = "0";
              printFrame.src = url ?? "";
              document.body.appendChild(printFrame);
              printFrame.onload = function () {
                printFrame.contentWindow?.focus();
                printFrame.contentWindow?.print();
                setTimeout(() => document.body.removeChild(printFrame), 1000);
              };
            }
          }}
        >
          <Print />
        </IconButton>
        )}
        <IconButton disabled={state.scale <= 0.3} onClick={zoomOut}>
          <ZoomOut />
        </IconButton>
        <IconButton disabled={state.scale >= 2} onClick={zoomIn}>
          <ZoomIn />
        </IconButton>
        <Button
          onClick={() => props.onClose(state.emailSent)}
          style={{ background: themeContext.theme?.palette.cancel?.main }}
        >
          {Vocabulary.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PreviewPDF;
